import React, { useCallback, useContext } from 'react';
import { BaseStep } from '../BaseStep';
import { Formik, Form, FormikHelpers, FormikProps } from 'formik';
import { FamilyEnrollmentContext } from 'features/family-enrollment/context';
import { GA_WEB_ENROLLMENT_EXPERIMENT_NAME, logParticipantEnrollment } from 'config/analytics';
import { Document } from '@contentful/rich-text-types';
import {
    FormikTextField,
    FormikTextFieldSecured,
    FormikCheckboxField,
} from 'components/fields/FormikTextField';
import {
    orgEnrollEmailValidator,
    orgEnrollPasswordValidator,
    confirmPasswordValidator,
} from 'common/modules/form-validation/validators';
import { FieldSpacer } from 'components/fields/FieldSpacer';
import { useBaseStyles, ButtonControls } from 'features/family-enrollment/steps/utils';
import { FormikSetPasswordField } from 'components/fields/FormikSetPasswordField';
import { useAppConfig } from 'common/features/app-config/utils/useAppConfig';
import { UserContent } from 'components/user-content/UserContent';
import { useContentfulGlobalContent } from 'common/api/contentful/global';
import { RegistrationRequest } from 'common/api/auth/models/RegistrationRequest';
import { validateEmail } from 'common/api/users/email/validate';
import { hasErrorWithStatus } from 'common/api/utils/hasErrorWithStatus';
import { StatusCode } from 'common/api/config';
import { openLoader, LoadingOverlay, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { ContentfulGlobal } from 'common/api/contentful/models/ContentfulGlobal';
import { link } from 'styles/typography';
import { Branding, IS_FLIPGIVE } from 'common/env';
import { ExternalLinks } from 'externalLinks';
import { createStyles } from 'utils/createStyle';
import { SocialLoginButtons } from 'components/social-login/SocialLoginButtons';
import {
    buildFamilyEnrollmentSocialRegistrationChallengeUrl,
    createSocialRegistrationChallenge,
    persistFamilyEnrollmentSocialRegistrationState,
    SocialRegistrationProvider,
} from 'features/family-enrollment/socialRegistration';

const marketingOptInStyles = createStyles({
    /** Scoped hack: top-align the checkbox box only in this field (Checkbox uses flex + items-center). */
    wrapper: [
        '',
        {
            '& div.flex.flex-row': { alignItems: 'flex-start' },
        },
    ],
});

interface SignInFormProps {
    email: string;
    marketingOptIn: boolean;
    password: string;
    confirmPassword: string;
}

const PasswordField = ({
    minAcceptableScore,
    globalContent,
}: {
    minAcceptableScore: number | null;
    globalContent: ContentfulGlobal | null;
}) => {
    return (
        <div>
            <FormikSetPasswordField
                name="password"
                label="Password (8+ characters)"
                maxLength={200}
                autoComplete="new-password"
                validate={orgEnrollPasswordValidator}
                minAcceptableScore={minAcceptableScore}
            />
            {globalContent && (
                <div className="text-xs text-grey-1 mt-3">
                    <UserContent content={globalContent?.setPasswordTooltip} />
                </div>
            )}
        </div>
    );
};

const MARKETING_OPT_IN_COPY =
    "Yes, I'd like to receive emails from FlipGive so I don't miss out on bonus earnings, online shopping deals, and new brand updates. I understand I can unsubscribe at any time.";
const FLIPGIVE_SUPPORT_PHONE = '800-727-4715';
const FLIPGIVE_SUPPORT_PHONE_OPTION = 'Option 3';
const FLIPGIVE_SUPPORT_EMAIL = 'support@flipgive.com';

const SignInForm = () => {
    const {
        goToNextStep,
        updateUserAccountInfo,
        userAccountInfo,
        loader,
        organization,
        isEnrollmentTestVariant,
        setSocialRegistrationError,
        setSocialRegistrationExistingAccountProvider,
        socialRegistrationProviders,
    } = useContext(FamilyEnrollmentContext);
    const { appConfig } = useAppConfig();
    const baseStyles = useBaseStyles();
    const globalContent = useContentfulGlobalContent();
    const googleProvider = socialRegistrationProviders.find(
        ({ provider }) => provider === 'google'
    );
    const appleProvider = socialRegistrationProviders.find(({ provider }) => provider === 'apple');
    const showSocialLoginButtons = Boolean(googleProvider || appleProvider);

    const isCanadianUser = organization?.countryCode === 'CA' || IS_FLIPGIVE;
    const showMarketingOptIn = isCanadianUser;

    const startSocialRegistration = useCallback(
        (provider: SocialRegistrationProvider, scheme?: string) => {
            setSocialRegistrationError(null);
            setSocialRegistrationExistingAccountProvider(null);

            const enrollmentCode = localStorage.getItem('RR_enrollmentCode') || '';
            const challenge = createSocialRegistrationChallenge();
            persistFamilyEnrollmentSocialRegistrationState({
                userAccountInfo,
                organization,
                enrollmentCode,
                codeVerifier: challenge.codeVerifier,
            });

            window.location.assign(
                buildFamilyEnrollmentSocialRegistrationChallengeUrl(provider, challenge, scheme)
            );
        },
        [
            organization,
            userAccountInfo,
            setSocialRegistrationError,
            setSocialRegistrationExistingAccountProvider,
        ]
    );

    // When visible (Canadian/FlipGive), default unchecked unless they already submitted this step (have email)
    const initialValues = {
        email: userAccountInfo?.email,
        marketingOptIn: showMarketingOptIn
            ? userAccountInfo?.email
                ? userAccountInfo?.marketingOptIn ?? false
                : false
            : false,
        password: userAccountInfo?.password,
        confirmPassword: userAccountInfo?.confirmPassword,
    } as SignInFormProps;

    const onSubmit = async (values: SignInFormProps, formik: FormikHelpers<SignInFormProps>) => {
        const { email } = values;

        openLoader(loader?.current as LoadingOverlay);
        const response = await validateEmail(email);
        closeLoader(loader?.current as LoadingOverlay);

        // CASE: user puts an email that already exists
        if (hasErrorWithStatus(response, StatusCode.Conflict)) {
            formik.setFieldError(
                'email',
                'Looks like this email is already associated with an account.'
            );
            return;
        }

        // CASE: all the data validates we can continue with the flow
        updateUserAccountInfo((currentState: RegistrationRequest) => ({
            ...currentState,
            ...values,
            marketingOptIn: showMarketingOptIn ? values.marketingOptIn : false,
        }));

        logParticipantEnrollment(
            isEnrollmentTestVariant ? 4 : 3,
            'account_details',
            {
                orgId: organization?.id,
                programType: organization?.type,
                flowVariant: isEnrollmentTestVariant ? 'variant' : 'control',
            },
            {
                experiment_name: GA_WEB_ENROLLMENT_EXPERIMENT_NAME,
                experiment_variant: isEnrollmentTestVariant ? 'variant' : 'control',
            }
        );
        goToNextStep();
    };

    return (
        <div className={baseStyles.formContainer}>
            {showSocialLoginButtons && (
                <SocialLoginButtons
                    onGoogleSignIn={() => startSocialRegistration('google', googleProvider?.scheme)}
                    onAppleSignIn={() => startSocialRegistration('apple', appleProvider?.scheme)}
                    showGoogle={Boolean(googleProvider)}
                    showApple={Boolean(appleProvider)}
                    googleLabel="Sign up with Google"
                    appleLabel="Sign up with Apple"
                />
            )}
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {(formikProps: FormikProps<SignInFormProps>) => (
                    <Form>
                        <FormikTextField
                            type="email"
                            name="email"
                            label="Email"
                            validate={orgEnrollEmailValidator}
                        />
                        <FieldSpacer />
                        <PasswordField
                            minAcceptableScore={appConfig.passwordPolicy.minimumZxcvbnScore}
                            globalContent={globalContent}
                        />
                        <FieldSpacer />
                        <FormikTextFieldSecured
                            name="confirmPassword"
                            label="Confirm Password"
                            maxLength={200}
                            autoComplete="new-password"
                            validate={confirmPasswordValidator(formikProps.values.password)}
                        />
                        {showMarketingOptIn && (
                            <>
                                <FieldSpacer />
                                <div className={marketingOptInStyles.wrapper}>
                                    <FormikCheckboxField
                                        name="marketingOptIn"
                                        id="marketingOptIn"
                                        label={MARKETING_OPT_IN_COPY}
                                        className="text-grey-1 text-sm font-semibold pl-1 leading-4"
                                        checked={formikProps.values.marketingOptIn}
                                    />
                                </div>
                            </>
                        )}
                        <div className="mt-6 text-xs text-grey-2">
                            By proceeding, I acknowledge I have read and agree to the {Branding}{' '}
                            <a
                                href={ExternalLinks.ParticipantTermsOfUse}
                                target="_blank"
                                rel="noreferrer"
                                className={link}
                            >
                                Participant Terms of Use
                            </a>
                            .
                        </div>
                        {showMarketingOptIn && IS_FLIPGIVE && (
                            <div className="mt-2 text-xs text-grey-2">
                                <a href={`tel:${FLIPGIVE_SUPPORT_PHONE}`} className={link}>
                                    {FLIPGIVE_SUPPORT_PHONE}
                                </a>{' '}
                                ({FLIPGIVE_SUPPORT_PHONE_OPTION}) |{' '}
                                <a href={`mailto:${FLIPGIVE_SUPPORT_EMAIL}`} className={link}>
                                    {FLIPGIVE_SUPPORT_EMAIL}
                                </a>
                            </div>
                        )}

                        <ButtonControls />
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export const SignInInfoStep = () => {
    const { stepHeaders, organization } = useContext(FamilyEnrollmentContext);

    return (
        <BaseStep
            contentClassName="text-md sm:text-lg font-thin"
            heading={stepHeaders?.signInInfoHeading as string}
            subheading={stepHeaders?.sIgnInInfoBody as Document}
            subheadingData={{ OrganizationName: organization?.name }}
        >
            <SignInForm />
        </BaseStep>
    );
};
